import {
  EtisStopDTO,
  useGetBobcatProductSets,
  useGetEtisJourneys,
  useGetEtisStops,
  useGetUserAllowedOrganizationTags,
} from '@/api/trafikportalen';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '@/components/core/Button';
import { Autocomplete } from '../Autocomplete';
import { IconArrowUpDown, IconSearch } from '@/icons';
import { useDebounce } from 'use-debounce';
import { DateTime } from 'luxon';
import { SelectableList } from '../SelectableList';
import { useRecentStops } from '@/hooks/useRecentStops';
import { useAutoAnimateConditional } from '@/hooks/useAutoAnimateConditional';
import { groupTicketsByProduct } from '@/helpers/groupTickets';
import type { BaseTicket } from '@/types/buyTicket';
import { toast } from '@/hooks/useToast';

const FRESH_JOURNEY_SEARCH_MS = 5 * 60 * 1000;

interface DynamicTicketsProps {
  selectedBaseTicketId: string | undefined;
  onSelectBaseTicket: (baseTicket: BaseTicket | undefined) => void;
}

interface ChosenStop {
  id: string;
  name: string;
}

export const DynamicTickets = ({
  selectedBaseTicketId,
  onSelectBaseTicket,
}: DynamicTicketsProps) => {
  const { formatMessage } = useIntl();
  const [fromStop, setFromStop] = useState<ChosenStop>({ id: '', name: '' });
  const [toStop, setToStop] = useState<ChosenStop>({ id: '', name: '' });
  const [directionKey, setDirectionKey] = useState(0);
  const [selectedJourney, setSelectedJourney] = useState<string>('');

  const { data: tags } = useGetUserAllowedOrganizationTags();

  const {
    data: journeyResult,
    isLoading: isJourneyLoading,
    error: journeyError,
    dataUpdatedAt: journeySearchedAt,
    refetch: refetchJourneys,
  } = useGetEtisJourneys(
    {
      originStopID: Number(fromStop.id),
      destinationStopID: Number(toStop.id),
    },
    {
      query: { enabled: fromStop.id !== '' && toStop.id !== '' },
    },
  );

  const journeyOptions = useMemo(() => {
    if (!journeyResult) {
      return [];
    }

    // Skip the first journey, as there are usually no tickets available for it
    return journeyResult.journeys.slice(1).map((journey) => {
      const departureTime = DateTime.fromISO(journey.departure_at);
      const departureString = departureTime.toFormat('yyyy-MM-dd HH:mm');
      const arrivalTime = DateTime.fromISO(journey.arrival_at);
      const arrivalString =
        arrivalTime.day !== departureTime.day
          ? arrivalTime.toFormat('yyyy-MM-dd HH:mm')
          : arrivalTime.toFormat('HH:mm');

      return {
        id: journey.id,
        label: `${departureString} - ${arrivalString}`,
        description: `${formatMessage({ defaultMessage: 'Zoner' })}: ${journey.zone_names || '-'}`,
      };
    });
  }, [journeyResult, formatMessage]);

  const {
    data: productSets,
    isLoading: isProductSetsLoading,
    error: productSetsError,
  } = useGetBobcatProductSets(
    {
      EtisJourneysResultKey: journeyResult?.journey_result_key || '',
      EtisJourneyID: selectedJourney,
    },
    {
      query: {
        enabled: !!journeyResult?.journey_result_key && !!selectedJourney,
        // An expired journey search, handled below by searching again.
        meta: { expectedErrorStatuses: [404] },
      },
    },
  );

  // The backend drops journey searches after a while, so a page left open
  // gets 404 for its tickets. Search again, but not for a fresh search, which
  // would only loop.
  useEffect(() => {
    if (productSetsError?.response?.status !== 404) return;
    if (Date.now() - journeySearchedAt < FRESH_JOURNEY_SEARCH_MS) return;

    toast.info({
      title: formatMessage({
        defaultMessage:
          'Sökningen hade gått ut och har gjorts om. Kontrollera vald resa.',
      }),
    });
    void refetchJourneys();
  }, [productSetsError, journeySearchedAt, refetchJourneys, formatMessage]);

  const baseTickets = useMemo(() => {
    if (!productSets || !tags) {
      return [];
    }

    const normalized = productSets.map((productSet) => {
      const pcTag = productSet.tags.find((tag) => tag.startsWith('pc_'));
      const productClassTag = tags.find((tag) => tag.tag_id === pcTag);
      const title = productClassTag
        ? `${productSet.title}, ${productClassTag.title}`
        : productSet.title;

      return {
        product_set_id: productSet.id,
        title,
        currency: productSet.currency,
        amount: productSet.amount,
        tags: productSet.tags,
      };
    });

    return groupTicketsByProduct(normalized);
  }, [productSets, tags]);

  const productSetOptions = useMemo(
    () =>
      baseTickets.map((baseTicket) => ({
        id: baseTicket.product_set_id,
        label: baseTicket.title,
      })),
    [baseTickets],
  );

  const noJourneysMessage = useMemo(() => {
    if (!fromStop.id || !toStop.id) {
      return formatMessage({
        defaultMessage: 'Välj start- och sluthållplats för att se resor',
      });
    }

    if (journeyResult?.journeys.length === 0 || journeyError) {
      return formatMessage({ defaultMessage: 'Inga matchande resor hittades' });
    }

    return undefined;
  }, [journeyResult, journeyError, formatMessage, fromStop.id, toStop.id]);

  const noProductSetsMessage = useMemo(() => {
    if (!selectedJourney) {
      return formatMessage({
        defaultMessage: 'Välj en resa för att se biljetter',
      });
    }

    if (productSetOptions.length === 0) {
      return formatMessage({ defaultMessage: 'Inga biljetter hittades' });
    }

    return undefined;
  }, [formatMessage, selectedJourney, productSetOptions]);

  const canSwapStops = fromStop.id !== '' || toStop.id !== '';

  const handleFromStopChange = useCallback(
    (stopId: string, stopName: string) => {
      setFromStop({ id: stopId, name: stopName });
    },
    [],
  );

  const handleToStopChange = useCallback((stopId: string, stopName: string) => {
    setToStop({ id: stopId, name: stopName });
  }, []);

  const handleSwapStops = useCallback(() => {
    setFromStop(toStop);
    setToStop(fromStop);
    setDirectionKey((key) => key + 1);
  }, [fromStop, toStop]);

  const noStopLabel = formatMessage({ defaultMessage: 'ingen hållplats' });
  const swapAnnouncement =
    directionKey === 0
      ? ''
      : formatMessage(
          { defaultMessage: 'Start är nu {from} och mål är {to}' },
          {
            from: fromStop.name || noStopLabel,
            to: toStop.name || noStopLabel,
          },
        );

  const handleSelect = useCallback(
    (productSetId: string) => {
      onSelectBaseTicket(
        baseTickets.find((ticket) => ticket.product_set_id === productSetId),
      );
    },
    [baseTickets, onSelectBaseTicket],
  );

  useEffect(() => {
    setSelectedJourney(journeyOptions.length > 0 ? journeyOptions[0].id : '');
  }, [journeyOptions]);

  useEffect(() => {
    if (baseTickets.length === 0) {
      if (selectedBaseTicketId) {
        onSelectBaseTicket(undefined);
      }
      return;
    }

    const isStillValid = baseTickets.some(
      (ticket) => ticket.product_set_id === selectedBaseTicketId,
    );
    if (!isStillValid) {
      onSelectBaseTicket(baseTickets[0]);
    }
  }, [baseTickets, selectedBaseTicketId, onSelectBaseTicket]);

  return (
    <div className="mt-5 flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <StopSelection
          key={`from-stop-${directionKey}`}
          id="fromStop"
          label={formatMessage({ defaultMessage: 'Från' })}
          selectedStopId={fromStop.id}
          selectedStopName={fromStop.name}
          onChange={handleFromStopChange}
          forbiddenStops={toStop.id ? [toStop.id] : undefined}
        />
        <div className="flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            startIcon={<IconArrowUpDown />}
            onClick={handleSwapStops}
            disabled={!canSwapStops}
          >
            <FormattedMessage defaultMessage="Byt hållplatser" />
          </Button>
        </div>
        <p className="sr-only" aria-live="polite" aria-atomic="true">
          {swapAnnouncement}
        </p>
        <StopSelection
          key={`to-stop-${directionKey}`}
          id="toStop"
          label={formatMessage({ defaultMessage: 'Till' })}
          selectedStopId={toStop.id}
          selectedStopName={toStop.name}
          onChange={handleToStopChange}
          forbiddenStops={fromStop.id ? [fromStop.id] : undefined}
        />
      </div>

      <div className="flex flex-col gap-2">
        <i className="text-text-800">
          <FormattedMessage defaultMessage="Biljetten blir giltig i de zoner som visas oavsett avgångstid." />
        </i>
        <SelectableList
          options={journeyOptions}
          selectedOption={selectedJourney}
          setSelectedOption={setSelectedJourney}
          noResultsMessage={noJourneysMessage}
          isLoading={isJourneyLoading}
        />

        <b className="text-text-900">
          <FormattedMessage defaultMessage="Välj framsökt biljett" />
        </b>
        <SelectableList
          options={productSetOptions}
          selectedOption={selectedBaseTicketId || ''}
          setSelectedOption={handleSelect}
          noResultsMessage={noProductSetsMessage}
          isLoading={isProductSetsLoading}
        />
      </div>
    </div>
  );
};

interface StopSelectionProps {
  onChange: (stopId: string, stopName: string) => void;
  label: string;
  id: string;
  selectedStopId: string;
  selectedStopName: string;
  forbiddenStops?: string[];
}

const StopSelection = ({
  onChange,
  label,
  id,
  selectedStopId,
  selectedStopName,
  forbiddenStops,
}: StopSelectionProps) => {
  const { formatMessage } = useIntl();
  const { recentStops, addRecentStop } = useRecentStops();
  const [recentStopsRef] = useAutoAnimateConditional<HTMLDivElement>();

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 300);
  const { data: etisStops } = useGetEtisStops(
    { searchString: debouncedSearch },
    {
      query: { enabled: debouncedSearch.length >= 2 },
    },
  );

  const [storedStops, setStoredStops] = useState<EtisStopDTO[]>([]);

  useEffect(() => {
    if (etisStops) {
      setStoredStops(etisStops);
    }
  }, [etisStops]);

  const { stopOptions, recentStopsOptions } = useMemo(() => {
    const recentStopsOptions = recentStops
      .filter((stop) => !forbiddenStops?.includes(String(stop.id)))
      .map((stop) => ({
        label: stop.name,
        value: stop.id,
      }));

    const storedStopsOptions = storedStops
      .filter((stop) => !forbiddenStops?.includes(String(stop.id)))
      .filter(
        (stop) => !recentStopsOptions.some((s) => s.value === String(stop.id)),
      )
      .map((stop) => ({
        label: stop.name,
        value: String(stop.id),
      }));

    const stopOptions = [...recentStopsOptions, ...storedStopsOptions];
    if (
      selectedStopId &&
      selectedStopName &&
      !forbiddenStops?.includes(selectedStopId) &&
      !stopOptions.some((stop) => stop.value === selectedStopId)
    ) {
      stopOptions.unshift({
        label: selectedStopName,
        value: selectedStopId,
      });
    }

    return {
      stopOptions,
      recentStopsOptions,
    };
  }, [
    storedStops,
    forbiddenStops,
    recentStops,
    selectedStopId,
    selectedStopName,
  ]);

  const handleValueChange = useCallback(
    (newValue: string) => {
      setSearch('');
      const stopName =
        stopOptions.find((stop) => stop.value === newValue)?.label ?? '';
      onChange(newValue, stopName);

      const selectedStop = storedStops.find(
        (stop) => String(stop.id) === newValue,
      );
      if (selectedStop) {
        addRecentStop(selectedStop);
      }
    },
    [onChange, storedStops, addRecentStop, stopOptions],
  );

  const handleSearchChange = useCallback(
    (newSearch: string) => {
      setSearch(newSearch);
      if (!newSearch) {
        onChange('', '');
      }
    },
    [onChange],
  );

  return (
    <div className="flex flex-col gap-2">
      <Autocomplete
        label={label}
        id={id}
        options={stopOptions}
        value={selectedStopId}
        onValueChange={handleValueChange}
        onSearchChange={handleSearchChange}
        placeholder={formatMessage({ defaultMessage: 'Sök hållplats' })}
        startAdornment={<IconSearch />}
      />
      {recentStopsOptions.length > 0 && (
        <div
          ref={recentStopsRef}
          className="flex w-full items-center overflow-x-auto pb-2"
        >
          <i className="text-nowrap text-sm text-text-800">
            <FormattedMessage defaultMessage="Tidigare val:" />
          </i>
          {recentStopsOptions.map((stop) => (
            <Button
              key={stop.value}
              variant="ghost"
              onClick={() => handleValueChange(stop.value)}
              className="min-w-fit px-3"
              size="sm"
              disabled={forbiddenStops?.includes(stop.value)}
            >
              {stop.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};
